import { useQuery } from '@apollo/client';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { GetRangeHistoryQuery, GetRangeHistoryQueryVariables, QuestMetricType } from '../client/generated/graphql';
import { getRangeHistoryQuery } from '../client/queries/rangeHistory';
import { ListGoal, ListQuest } from '../client/types';
import { getTargetXP } from '../helpers/goal';
import { useCachedQuestsWithFallback } from './caching/quest';

export type DailyGoalStats = {
    xpForToday: number;
    targetForToday: number;
};

export const useDailyGoalProgress = (
    goals: ListGoal[],
    goalCompletion: string,
): { data: Record<string, DailyGoalStats> | null; loading: boolean } => {
    const weekDay = dayjs().day();
    const endDate = dayjs(goalCompletion);
    const startDate = endDate.subtract(1, 'week');
    const { data: historyData, loading: queryLoading } = useQuery<GetRangeHistoryQuery, GetRangeHistoryQueryVariables>(
        getRangeHistoryQuery,
        {
            variables: {
                start: startDate,
                end: endDate,
            },
        },
    );

    const logs = historyData?.eventLogs ?? [];

    const questIdsFromLogs = useMemo(() => {
        const ids = new Set<string>();
        logs.forEach(log => {
            log.questMetricIncreased.forEach(metric => {
                if (metric.questId) ids.add(metric.questId);
            });
        });
        return Array.from(ids);
    }, [logs]);

    const { quests, loading: questsLoading } = useCachedQuestsWithFallback(questIdsFromLogs);
    const questMap: Record<string, ListQuest> = Object.fromEntries(quests.map(q => [q.id, q] as [string, ListQuest]));

    const loading = queryLoading || questsLoading;

    // memoize the stats map to avoid unstable references
    const statsMap = useMemo(() => {
        // initialize stats for each goal
        const initialStats: Record<string, DailyGoalStats> = goals.reduce((acc, goal) => {
            acc[goal.id] = { xpForToday: 0, targetForToday: getTargetXP(goal, weekDay) };
            return acc;
        }, {} as Record<string, DailyGoalStats>);
        // filter logs for current day only using isSame
        const todayLogs = historyData?.eventLogs.filter(log => dayjs(log.createdAt).isSame(dayjs(), 'day')) ?? [];
        // flatten XP increases
        const increases = todayLogs
            .flatMap(log => log.questMetricIncreased)
            .filter(inc => inc.questMetricType === QuestMetricType.Experience);
        // accumulate XP per goal
        return increases.reduce((acc, inc) => {
            const quest = questMap[inc.questId];
            const goalId = quest?.goalId;
            if (quest && goalId && acc[goalId]) {
                acc[goalId].xpForToday += inc.addedValue;
            }
            return acc;
        }, initialStats);
    }, [historyData?.eventLogs, goals, goalCompletion, questMap, weekDay]);

    return {
        data: loading ? null : statsMap,
        loading,
    };
};
